import { getCLS, getFID, getLCP, getFCP, getTTFB } from 'web-vitals';

// Types for analytics events
export interface AnalyticsEvent {
  name: string;
  properties?: Record<string, any>;
  timestamp?: number;
}

export interface WebVitalsMetric {
  name: string;
  value: number;
  delta: number;
  id: string;
}

class Analytics {
  private static instance: Analytics;
  private initialized: boolean = false;
  private queue: AnalyticsEvent[] = [];
  private userId: string | null = null;
  private failedEvents: AnalyticsEvent[] = [];
  private retryTimeout: number | null = null;

  private constructor() {}

  static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  init(userId?: string) {
    if (this.initialized) return;
    
    this.userId = userId || null;
    this.initialized = true;
    
    // Initialize web vitals monitoring
    this.measureWebVitals();
    
    // Process any queued events
    this.processQueue();
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  private async processQueue() {
    while (this.queue.length > 0) {
      const event = this.queue.shift();
      if (event) {
        await this.sendEvent(event);
      }
    }
  }

  private async sendEvent(event: AnalyticsEvent, isRetry: boolean = false) {
    try {
      const payload = {
        ...event,
        userId: this.userId,
        timestamp: event.timestamp || Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      // In development, just log the event
      if (import.meta.env.DEV) {
        console.log('Analytics event:', payload);
        return;
      }

      // Only attempt to send to server in production
      if (import.meta.env.PROD) {
        const response = await fetch('/api/analytics', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`Analytics request failed with status ${response.status}`);
        }
      }

      // If this was a retry and it succeeded, remove from failed events
      if (isRetry) {
        this.failedEvents = this.failedEvents.filter(e => e !== event);
      }
    } catch (error) {
      if (!isRetry) {
        // Add to failed events queue for retry
        this.failedEvents.push(event);
        this.scheduleRetry();
      }
      // Log error but don't throw - we don't want analytics failures to break the app
      console.warn('Analytics event failed to send:', error);
    }
  }

  private scheduleRetry() {
    // Only schedule one retry at a time
    if (this.retryTimeout !== null) return;

    this.retryTimeout = window.setTimeout(() => {
      this.retryFailedEvents();
      this.retryTimeout = null;
    }, 60000); // Retry after 1 minute
  }

  private async retryFailedEvents() {
    const events = [...this.failedEvents];
    for (const event of events) {
      await this.sendEvent(event, true);
    }
  }

  trackEvent(name: string, properties?: Record<string, any>) {
    const event: AnalyticsEvent = {
      name,
      properties,
      timestamp: Date.now(),
    };

    if (!this.initialized) {
      this.queue.push(event);
      return;
    }

    this.sendEvent(event);
  }

  private handleWebVitals = (metric: WebVitalsMetric) => {
    this.trackEvent('web_vitals', {
      metric_name: metric.name,
      value: metric.value,
      delta: metric.delta,
      id: metric.id,
    });
  };

  private measureWebVitals() {
    getCLS(this.handleWebVitals);
    getFID(this.handleWebVitals);
    getLCP(this.handleWebVitals);
    getFCP(this.handleWebVitals);
    getTTFB(this.handleWebVitals);
  }

  // Track specific events
  trackPageView(path: string) {
    this.trackEvent('page_view', { path });
  }

  trackConversation(questionLength: number, responseTime: number) {
    this.trackEvent('conversation', {
      question_length: questionLength,
      response_time: responseTime,
    });
  }

  trackError(error: Error, context?: Record<string, any>) {
    this.trackEvent('error', {
      message: error.message,
      stack: error.stack,
      ...context,
    });
  }

  trackPerformance(name: string, duration: number, context?: Record<string, any>) {
    this.trackEvent('performance', {
      name,
      duration,
      ...context,
    });
  }
}

export const analytics = Analytics.getInstance();